import { useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Volume2, Download, ArrowLeft, Play, Pause, Loader2, History } from 'lucide-react'

interface SpeechResult {
  success: boolean
  audio_url: string
  text: string
  voice_id: string
  emotion: string
  language_boost: string
}

const voiceOptions = [
  { value: 'Deep_Voice_Man', label: 'Deep Voice Man' },
  { value: 'Wise_Woman', label: 'Wise Woman' },
  { value: 'Friendly_Person', label: 'Friendly Person' },
  { value: 'Inspirational_girl', label: 'Inspirational Girl' },
  { value: 'Calm_Woman', label: 'Calm Woman' },
  { value: 'Casual_Guy', label: 'Casual Guy' },
  { value: 'Lively_Girl', label: 'Lively Girl' },
  { value: 'Patient_Man', label: 'Patient Man' },
  { value: 'Young_Knight', label: 'Young Knight' },
  { value: 'Determined_Man', label: 'Determined Man' },
  { value: 'Lovely_Girl', label: 'Lovely Girl' },
  { value: 'Decent_Boy', label: 'Decent Boy' },
  { value: 'Imposing_Manner', label: 'Imposing Manner' },
  { value: 'Elegant_Man', label: 'Elegant Man' },
  { value: 'Abbess', label: 'Abbess' },
  { value: 'Sweet_Girl_2', label: 'Sweet Girl 2' },
  { value: 'Exuberant_Girl', label: 'Exuberant Girl' }
]

const emotionOptions = [
  { value: 'auto', label: 'Auto' },
  { value: 'angry', label: 'Angry' },
  { value: 'happy', label: 'Happy' },
  { value: 'sad', label: 'Sad' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'fearful', label: 'Fearful' },
  { value: 'disgusted', label: 'Disgusted' },
  { value: 'surprised', label: 'Surprised' }
]

const languageOptions = [
  { value: 'Chinese', label: 'Chinese' },
  { value: 'English', label: 'English' }
]

export function TTSPPage() {
  const [text, setText] = useState('')
  const [voiceId, setVoiceId] = useState('Deep_Voice_Man')
  const [emotion, setEmotion] = useState('auto')
  const [languageBoost, setLanguageBoost] = useState('Chinese')

  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedAudio, setGeneratedAudio] = useState<SpeechResult | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const audioRef = useRef<HTMLAudioElement>(null)

  const handleGenerate = useCallback(async () => {
    if (!text.trim()) return

    setIsGenerating(true)
    setGeneratedAudio(null)

    try {
      const { apiClient } = await import('../utils/api')

      const response = await apiClient.generateSpeech(text.trim(), voiceId, emotion, languageBoost)

      if (response.success) {
        setGeneratedAudio(response)
      } else {
        console.error('Speech generation failed:', response)
        alert('Speech generation failed. Please try again.')
      }
    } catch (error) {
      console.error('Error generating speech:', error)
      alert('Failed to generate speech. Please try again.')
    } finally {
      setIsGenerating(false)
    }
  }, [text, voiceId, emotion, languageBoost])

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current || !generatedAudio) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play()
      setIsPlaying(true)
    }
  }, [isPlaying, generatedAudio])

  const handleDownload = useCallback(async () => {
    if (!generatedAudio) return

    try {
      // Fetch the audio data
      const response = await fetch(generatedAudio.audio_url)
      const blob = await response.blob()

      // Create a blob URL for download
      const blobUrl = URL.createObjectURL(blob)

      // Create download link
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `speech-${Date.now()}.wav`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Failed to download audio:', error)
      // Fallback to opening in new tab if download fails
      window.open(generatedAudio.audio_url, '_blank')
    }
  }, [generatedAudio])

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false)
  }, [])

  return (
    <div className="min-h-screen bg-[#0d1117] text-white">
      {/* Header */}
      <header className="border-b border-cyan-900/30 bg-[#161b22]">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>主页</span>
              </Link>
              <div className="h-6 w-px bg-white/20"></div>
              <Volume2 className="w-8 h-8 text-cyan-400" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Text-to-Speech
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <Link
                to="/history?media_type=audio"
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-cyan-900/20 hover:bg-cyan-900/30 border border-cyan-900/30 transition-colors"
              >
                <History className="w-4 h-4" />
                <span>历史记录</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-h-[600px]">
            {/* Left Panel - Input */}
            <div className="space-y-6">
              <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30">
                <h2 className="text-lg font-semibold mb-4 text-white">语音生成设置</h2>

                {/* Text Input */}
                <div className="space-y-2 mb-6">
                  <label className="block text-sm font-medium text-white">
                    文本内容
                  </label>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="请输入要转换为语音的文本..."
                    className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none min-h-[120px]"
                    rows={4}
                  />
                  <p className="text-xs text-gray-400">
                    {text.length} 字符
                  </p>
                </div>

                {/* Voice Selection */}
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-medium text-white">
                    语音选择
                  </label>
                  <select
                    value={voiceId}
                    onChange={(e) => setVoiceId(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    {voiceOptions.map((option) => (
                      <option key={option.value} value={option.value} className="bg-[#161b22]">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Emotion Selection */}
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-medium text-white">
                    情感选择
                  </label>
                  <select
                    value={emotion}
                    onChange={(e) => setEmotion(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    {emotionOptions.map((option) => (
                      <option key={option.value} value={option.value} className="bg-[#161b22]">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Language Selection */}
                <div className="space-y-2 mb-6">
                  <label className="block text-sm font-medium text-white">
                    语言优化
                  </label>
                  <select
                    value={languageBoost}
                    onChange={(e) => setLanguageBoost(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    {languageOptions.map((option) => (
                      <option key={option.value} value={option.value} className="bg-[#161b22]">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={!text.trim() || isGenerating}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-700 disabled:to-gray-700 text-white rounded-lg font-medium transition-all duration-200 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>生成中...</span>
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-5 h-5" />
                      <span>生成语音</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Right Panel - Audio Player & Download */}
            <div className="space-y-6">
              {generatedAudio ? (
                <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30">
                  <h3 className="text-lg font-semibold mb-4 text-white">生成的语音</h3>

                  {/* Audio Info */}
                  <div className="mb-4 p-3 bg-[#0d1117] rounded-lg">
                    <div className="text-sm text-gray-300 space-y-1">
                      <p><span className="font-medium">语音:</span> {voiceOptions.find(v => v.value === generatedAudio.voice_id)?.label}</p>
                      <p><span className="font-medium">情感:</span> {emotionOptions.find(e => e.value === generatedAudio.emotion)?.label}</p>
                      <p><span className="font-medium">语言:</span> {languageOptions.find(l => l.value === generatedAudio.language_boost)?.label}</p>
                    </div>
                  </div>

                  {/* Audio Player */}
                  <div className="mb-4">
                    <audio
                      ref={audioRef}
                      src={generatedAudio.audio_url}
                      onEnded={handleAudioEnded}
                      className="hidden"
                    />
                    <button
                      onClick={handlePlayPause}
                      className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-cyan-900/20 hover:bg-cyan-900/30 border border-cyan-900/30 text-white rounded-lg font-medium transition-colors"
                    >
                      {isPlaying ? (
                        <>
                          <Pause className="w-5 h-5" />
                          <span>暂停</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5" />
                          <span>播放</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Download Button */}
                  <button
                    onClick={handleDownload}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-medium transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    <span>下载音频</span>
                  </button>
                </div>
              ) : (
                <div className="bg-[#161b22] rounded-xl p-8 border border-cyan-900/30 text-center">
                  <Volume2 className="w-16 h-16 text-cyan-400 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold text-white mb-2">等待生成</h3>
                  <p className="text-gray-400">在左侧输入文本并配置参数，然后点击"生成语音"按钮</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
